import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, Plus, Leaf, MapPin } from 'lucide-react';
import { journalApi, plantsApi, zonesApi } from '../services/gardenApi';
import { useGardenPageTitle } from '../useGardenPageTitle';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function groupByMonth(entries) {
  const map = new Map();
  for (const e of entries) {
    const d = new Date(e.entry_date);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const key = `${y}-${m}`;
    if (!map.has(key)) map.set(key, { year: y, month: m, entries: [] });
    map.get(key).entries.push(e);
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year; // newest first
    return b.month - a.month;
  });
}

function fmtDay(iso) {
  const d = new Date(iso);
  return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}

export default function JournalScreen() {
  useGardenPageTitle('Garden journal', 'Garden journal');
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const plantFilter = search.get('plant_id') || '';
  const zoneFilter = search.get('zone_id') || '';

  const [entries, setEntries] = useState([]);
  const [plants, setPlants] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [j, p, z] = await Promise.all([
        journalApi.list({
          plant_id: plantFilter || undefined,
          zone_id: zoneFilter || undefined,
        }),
        plantsApi.list().catch(() => ({ plants: [] })),
        zonesApi.list().catch(() => ({ zones: [] })),
      ]);
      setEntries(j.entries || []);
      setPlants(p.plants || []);
      setZones(z.zones || []);
    } catch (e) {
      setError(e.message || 'Failed to load journal');
    } finally {
      setLoading(false);
    }
  }, [plantFilter, zoneFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => groupByMonth(entries), [entries]);

  const activeFilter = useMemo(() => {
    if (plantFilter) {
      const p = plants.find((x) => x.id === plantFilter);
      return { kind: 'plant', label: p?.common_name || 'Plant', id: plantFilter };
    }
    if (zoneFilter) {
      const z = zones.find((x) => x.id === zoneFilter);
      return { kind: 'zone', label: z?.name || 'Zone', id: zoneFilter };
    }
    return null;
  }, [plantFilter, zoneFilter, plants, zones]);

  const clearFilter = () => navigate('/garden/journal');

  return (
    <div className="relative">
      <header className="flex items-center justify-between px-4 pt-3 pb-2">
        <h1 className="text-lg font-semibold text-garden-ink">Garden journal</h1>
        <button
          onClick={() => navigate('/garden/journal/new')}
          className="min-h-11 min-w-11 -mr-2 rounded-full flex items-center justify-center text-garden-primary"
          aria-label="Add entry"
        >
          <Plus size={24} />
        </button>
      </header>

      {activeFilter ? (
        <div className="px-4 pb-2">
          <button
            onClick={clearFilter}
            className="inline-flex items-center gap-1.5 px-3 min-h-8 rounded-full bg-garden-primary/10 text-garden-primary text-xs font-medium"
          >
            {activeFilter.kind === 'plant'
              ? <Leaf size={12} />
              : <MapPin size={12} />}
            {activeFilter.label}
            <span className="opacity-60">{'\u00d7'}</span>
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>
      ) : entries.length === 0 ? (
        <EmptyJournal onAdd={() => navigate('/garden/journal/new')} filtered={!!activeFilter} />
      ) : (
        <div className="pb-24">
          {grouped.map(({ year, month, entries: monthEntries }) => (
            <section key={`${year}-${month}`} className="mb-3">
              <div className="px-4 pt-2 pb-1">
                <h2 className="text-xs uppercase tracking-wide font-semibold text-garden-muted">
                  {MONTH_NAMES[month - 1]} {year}
                </h2>
              </div>
              <ul className="mx-3 space-y-2">
                {monthEntries.map((e) => (
                  <EntryCard
                    key={e.id}
                    entry={e}
                    onOpen={() => navigate(`/garden/journal/${e.id}`)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {entries.length > 0 ? (
        <div className="fixed bottom-20 right-4 z-20">
          <button
            onClick={() => navigate('/garden/journal/new')}
            className="w-14 h-14 rounded-full bg-garden-primary text-white shadow-lg flex items-center justify-center active:scale-95 transition"
            aria-label="Add entry"
          >
            <Plus size={28} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function EntryCard({ entry, onOpen }) {
  const photos = entry.photo_urls || [];
  const preview = photos.slice(0, 3);
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className="bg-white rounded-2xl border border-garden-ink/10 p-3 cursor-pointer hover:border-garden-primary/40 focus:border-garden-primary/40 focus:outline-none transition-colors"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-garden-primary">
          {fmtDay(entry.entry_date)}
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-garden-muted">
          {entry.plant_name ? (
            <span className="inline-flex items-center gap-1"><Leaf size={11} />{entry.plant_name}</span>
          ) : null}
          {entry.zone_name ? (
            <span className="inline-flex items-center gap-1"><MapPin size={11} />{entry.zone_name}</span>
          ) : null}
        </div>
      </div>
      {entry.body_text ? (
        <p className="text-sm text-garden-ink line-clamp-3 whitespace-pre-wrap">
          {entry.body_text}
        </p>
      ) : (
        <p className="text-sm italic text-garden-muted">{'(photo-only entry)'}</p>
      )}
      {preview.length > 0 ? (
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {preview.map((url, idx) => (
            <div
              key={url}
              className="relative aspect-square rounded-lg overflow-hidden bg-garden-surface"
            >
              <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
              {idx === 2 && photos.length > 3 ? (
                <div className="absolute inset-0 bg-black/40 text-white text-xs font-semibold flex items-center justify-center">
                  +{photos.length - 3}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function EmptyJournal({ onAdd, filtered }) {
  return (
    <div className="px-6 py-12 flex flex-col items-center text-center">
      <div className="w-14 h-14 rounded-full bg-garden-primary/10 text-garden-primary flex items-center justify-center mb-3">
        <BookOpen size={28} />
      </div>
      <h2 className="text-lg font-semibold text-garden-ink">
        {filtered ? 'No entries for this filter' : 'No journal entries yet'}
      </h2>
      <p className="mt-2 text-sm text-garden-muted max-w-xs">
        {filtered
          ? 'Add an entry or clear the filter to see your whole journal.'
          : 'Jot quick notes and snap photos as you work in the garden \u2014 they\u2019ll be grouped by month and linkable to your plants and zones.'}
      </p>
      <button
        onClick={onAdd}
        className="mt-5 min-h-11 px-5 rounded-full bg-garden-primary text-white text-sm font-medium active:scale-95 transition inline-flex items-center gap-1.5"
      >
        <Plus size={18} /> New entry
      </button>
    </div>
  );
}
